import { useCallback, useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { prefetchHandlers } from './routePrefetch'
import {
  Activity,
  BookOpen,
  Bot,
  Building,
  Building2,
  CalendarClock,
  ChevronDown,
  Clapperboard,
  Contact,
  FileSignature,
  FileText,
  Globe,
  Handshake,
  House,
  Inbox,
  KanbanSquare,
  KeyRound,
  Landmark,
  LayoutDashboard,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  UserRound,
  Users,
  Wallet,
  Wrench,
  type LucideIcon
} from 'lucide-react'
import { useAuth } from '@/providers/auth'
import { usePermission } from '@/providers/permissions'
import { useChatNotifications } from '@/providers/chatNotifications'
import { useListingIntakeCounts } from '@/hooks/useListingIntakeCounts'
import { useMyAccess } from '@/hooks/useMyAccess'
import { NAV_GROUP } from '@/lib/breadcrumbs'
import { allowedDashboards, type DashboardKey } from '@/lib/dashboards'
import { cn } from '@/lib/utils'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  badge?: number
}

interface NavGroup {
  id: string
  label: string
  icon: LucideIcon
  items: NavItem[]
  flat?: boolean
}

const ADMIN_GROUP = 'admin'

const DASHBOARD_ICON: Record<DashboardKey, LucideIcon> = {
  site: Globe,
  vendas: KanbanSquare,
  financeiro: Wallet
}

const isUnder = (pathname: string, to: string) => pathname === to || pathname.startsWith(`${to}/`)

function activeItemOf(groups: NavGroup[], pathname: string): string | null {
  const matches = groups.flatMap(group => group.items.map(item => item.to)).filter(to => isUnder(pathname, to))
  return matches.sort((a, b) => b.length - a.length)[0] ?? null
}

function useNavGroups(): NavGroup[] {
  const { company } = useAuth()
  const { hasPermission } = usePermission()
  const { data: access } = useMyAccess()
  const { total: chatUnread } = useChatNotifications()

  const inCompany = !!company?.id
  const can = (resource: string) => inCompany && hasPermission(resource, 'read')
  const canRealState = can('real-state')
  const { data: intakeCounts } = useListingIntakeCounts(canRealState)
  const canCustomers = can('customers')
  const canCrm = can('crm')
  const canChat = can('chat')
  const canRentals = can('rentals')
  const canDocuments = can('documents')
  const canBlog = can('blog')
  const canSettings = can('settings')
  const settings = company?.id ? `/empresas/${company.id}/configuracoes` : null

  const groups: NavGroup[] = [
    {
      id: 'dashboard',
      label: NAV_GROUP.dashboard,
      icon: LayoutDashboard,
      items: allowedDashboards(can).map(dashboard => ({ to: dashboard.path, label: dashboard.label, icon: DASHBOARD_ICON[dashboard.key] }))
    },
    {
      id: 'portfolio',
      label: NAV_GROUP.portfolio,
      icon: House,
      items: canRealState
        ? [
            { to: '/imoveis', label: 'Imóveis', icon: Building2 },
            { to: '/imoveis/captacoes', label: 'Captações', icon: Inbox, badge: intakeCounts?.new ?? 0 },
            { to: '/imoveis/chaves', label: 'Chaves', icon: KeyRound },
            { to: '/imoveis/proprietarios', label: 'Proprietários', icon: Contact }
          ]
        : []
    },
    {
      id: 'relationship',
      label: NAV_GROUP.relationship,
      icon: Users,
      items: [
        ...(canCustomers ? [{ to: '/clientes', label: 'Clientes', icon: UserRound }] : []),
        ...(canCrm ? [{ to: '/crm', label: 'CRM', icon: KanbanSquare }] : []),
        ...(canChat ? [{ to: '/chat', label: 'Chat', icon: MessageCircle, badge: chatUnread }] : []),
        ...(settings && canSettings ? [{ to: `${settings}/atendente-ia`, label: 'Atendente IA', icon: Bot }] : [])
      ]
    },
    {
      id: 'leasing',
      label: NAV_GROUP.leasing,
      icon: FileSignature,
      items: canRentals
        ? [
            { to: '/contratos', label: 'Contratos', icon: FileText },
            { to: '/financeiro', label: 'Financeiro', icon: Wallet },
            { to: '/financeiro/recebimentos', label: 'Recebimentos', icon: Landmark }
          ]
        : []
    },
    {
      id: 'tools',
      label: NAV_GROUP.tools,
      icon: Wrench,
      items: [
        ...(canRealState ? [{ to: '/studio', label: 'Studio', icon: Clapperboard }] : []),
        ...(canBlog ? [{ to: '/blog', label: 'Blog', icon: BookOpen }] : []),
        ...(canDocuments ? [{ to: '/documentos/modelos', label: 'Modelos', icon: FileText }] : [])
      ]
    },
    {
      id: 'website',
      label: NAV_GROUP.website,
      icon: Globe,
      flat: true,
      items: settings && canSettings ? [{ to: `${settings}/site`, label: NAV_GROUP.website, icon: Globe }] : []
    },
    {
      id: ADMIN_GROUP,
      label: NAV_GROUP.admin,
      icon: ShieldCheck,
      items: access?.isSuperAdmin
        ? [
            { to: '/administracao/imobiliarias', label: 'Imobiliárias', icon: Building },
            { to: '/administracao/agendamentos', label: 'Agendamentos', icon: CalendarClock },
            { to: '/administracao/saude', label: 'Saúde dos serviços', icon: Activity },
            { to: '/administracao/vendas', label: 'Vendas', icon: Handshake },
            { to: '/administracao/ia', label: 'IA', icon: Sparkles }
          ]
        : []
    }
  ]

  return groups.filter(group => group.items.length > 0)
}

function useOpenGroups(activeGroup: string | undefined) {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (activeGroup) setOpenGroups(current => (current[activeGroup] ? current : { ...current, [activeGroup]: true }))
  }, [activeGroup])

  const setGroupOpen = useCallback((id: string, open: boolean) => setOpenGroups(current => ({ ...current, [id]: open })), [])

  return { openGroups, setGroupOpen }
}

function CountBadge({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <span className="tnum ml-auto grid h-[18px] min-w-[18px] place-items-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
      {count > 99 ? '99+' : count}
    </span>
  )
}

function RailGroup({ group, active, onNavigate }: { group: NavGroup; active: string | null; onNavigate?: () => void }) {
  const { label, icon: Icon, items } = group
  const routeActive = items.some(item => item.to === active)
  const badge = items.reduce((sum, item) => sum + (item.badge ?? 0), 0)

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger
            aria-label={badge > 0 ? `${label}, ${badge} ${badge === 1 ? 'pendência' : 'pendências'}` : label}
            data-tour={group.id}
            className={cn(
              'relative flex h-10 w-10 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              routeActive ? 'bg-sidebar-accent text-primary shadow-xs' : 'text-sidebar-foreground hover:bg-sidebar-accent/70 hover:text-foreground'
            )}
          >
            <Icon className="h-[18px] w-[18px]" />
            {badge > 0 && <span aria-hidden className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-destructive ring-2 ring-sidebar" />}
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="right" className="text-xs">
          {label}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent side="right" align="start" sideOffset={10} className="w-56">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{label}</DropdownMenuLabel>
        {items.map(({ to, label: itemLabel, icon: ItemIcon, badge: itemBadge }) => (
          <DropdownMenuItem key={to} asChild>
            <Link
              to={to}
              onClick={onNavigate}
              {...prefetchHandlers(to)}
              className={cn('flex cursor-pointer items-center gap-2.5', to === active && 'font-semibold text-primary')}
            >
              <ItemIcon className="h-4 w-4" />
              <span className="flex-1">{itemLabel}</span>
              <CountBadge count={itemBadge ?? 0} />
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SidebarGroup({
  group,
  active,
  open,
  onOpenChange,
  onNavigate
}: {
  group: NavGroup
  active: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onNavigate?: () => void
}) {
  const { id, label, icon: Icon, items } = group
  const routeActive = items.some(item => item.to === active)
  const badge = items.reduce((sum, item) => sum + (item.badge ?? 0), 0)

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
        aria-controls={`nav-group-${id}`}
        data-tour={id}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          routeActive && !open ? 'text-primary' : 'text-sidebar-foreground hover:bg-sidebar-accent/70 hover:text-foreground'
        )}
      >
        <Icon className="h-[18px] w-[18px] shrink-0" />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {!open && <CountBadge count={badge} />}
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div id={`nav-group-${id}`} className="ml-[21px] mt-0.5 flex flex-col gap-1 border-l border-sidebar-border pl-2">
          {items.map(({ to, label: itemLabel, icon: ItemIcon, badge: itemBadge }) => {
            const current = to === active
            return (
              <NavLink
                key={to}
                to={to}
                onClick={onNavigate}
                {...prefetchHandlers(to)}
                aria-current={current ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  current
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground shadow-xs ring-1 ring-sidebar-border'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent/70 hover:text-foreground'
                )}
              >
                <ItemIcon className={cn('h-4 w-4 shrink-0', current ? 'text-primary' : 'text-muted-foreground')} />
                <span className="min-w-0 flex-1 truncate">{itemLabel}</span>
                <CountBadge count={itemBadge ?? 0} />
              </NavLink>
            )
          })}
        </div>
      )}
    </div>
  )
}

function TopLink({
  to,
  label,
  icon: Icon,
  current,
  tour,
  collapsed,
  onNavigate
}: {
  to: string
  label: string
  icon: LucideIcon
  current: boolean
  tour?: string
  collapsed?: boolean
  onNavigate?: () => void
}) {
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={to}
            onClick={onNavigate}
            {...prefetchHandlers(to)}
            data-tour={tour}
            aria-label={label}
            aria-current={current ? 'page' : undefined}
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              current ? 'bg-sidebar-accent text-primary shadow-xs' : 'text-sidebar-foreground hover:bg-sidebar-accent/70 hover:text-foreground'
            )}
          >
            <Icon className="h-[18px] w-[18px]" />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" className="text-xs">
          {label}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Link
      to={to}
      onClick={onNavigate}
      {...prefetchHandlers(to)}
      data-tour={tour}
      aria-current={current ? 'page' : undefined}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        current
          ? 'bg-sidebar-accent text-sidebar-accent-foreground shadow-xs ring-1 ring-sidebar-border'
          : 'text-sidebar-foreground hover:bg-sidebar-accent/70 hover:text-foreground'
      )}
    >
      <Icon className={cn('h-[18px] w-[18px] shrink-0', current && 'text-primary')} />
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </Link>
  )
}

export function SidebarNav({ collapsed, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  const groups = useNavGroups()
  const { pathname } = useLocation()
  const active = activeItemOf(groups, pathname)
  const activeGroup = groups.find(group => group.items.some(item => item.to === active))?.id
  const { openGroups, setGroupOpen } = useOpenGroups(activeGroup)
  const tenantGroups = groups.filter(group => group.id !== ADMIN_GROUP)
  const adminGroups = groups.filter(group => group.id === ADMIN_GROUP)
  const mainGroups = tenantGroups.length > 0 ? tenantGroups : adminGroups
  const footerGroups = tenantGroups.length > 0 ? adminGroups : []

  const renderGroup = (group: NavGroup) =>
    group.flat ? (
      group.items.map(item => (
        <TopLink
          key={item.to}
          to={item.to}
          label={item.label}
          icon={item.icon}
          current={item.to === active}
          tour={group.id}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />
      ))
    ) : collapsed ? (
      <RailGroup key={group.id} group={group} active={active} onNavigate={onNavigate} />
    ) : (
      <SidebarGroup
        key={group.id}
        group={group}
        active={active}
        open={openGroups[group.id] ?? group.id === activeGroup}
        onOpenChange={open => setGroupOpen(group.id, open)}
        onNavigate={onNavigate}
      />
    )

  return (
    <>
      <nav aria-label="Menu principal" className={cn('flex flex-1 flex-col gap-1 overflow-y-auto p-3', collapsed && 'items-center')}>
        {mainGroups.map(renderGroup)}
      </nav>
      {footerGroups.length > 0 && (
        <nav
          aria-label={NAV_GROUP.admin}
          className={cn('flex shrink-0 flex-col gap-1 border-t border-sidebar-border p-3', collapsed && 'items-center')}
        >
          {footerGroups.map(renderGroup)}
        </nav>
      )}
    </>
  )
}
